/**
 * A websocket that never gives up. Reconnects with backoff (at once when the network comes back or
 * the screen is looked at again), tells the client when it is open or offline, and never throws on
 * send: a send while offline returns false and the client queues.
 */

import type { ClientMessage, ServerMessage } from "../../../shared/protocol";

export type Status = "connecting" | "open" | "offline";

/** The connection in words, the same on every screen: the pill in the corner. */
export const STATUS_WORDS: Record<Status, string> = { connecting: "Connecting", open: "Live", offline: "Offline" };

export interface Transport {
  connect(onMessage: (m: ServerMessage) => void, onStatus: (s: Status) => void): void;
  send(m: ClientMessage): boolean;
  close(): void;
  /**
   * The socket says it is open but nothing has come back through it (the watchdog in client.ts):
   * drop it, say Offline, and open a new one at once.
   */
  restart(): void;
  /**
   * Send a player's picture (a small JPEG), with that player's session (decision 39). Resolves to
   * null when stored, or the reason it was not.
   */
  uploadPhoto(playerId: string, jpeg: Blob, session: string | null): Promise<string | null>;
  /** Where a player's picture is served from. */
  photoUrl(playerId: string, version: number): string;
}

/** Refused this many times in a row without once getting in, a screen asks whether its link has stopped. */
const ASK_AFTER = 3;

/** A socket that has not opened in this long is given up and tried again: a handshake can hang on a stalled network. */
const OPEN_WITHIN = 10_000;

/** What a screen is told when its link has stopped while it was away (asleep, closed, reloaded). */
export function stoppedReason(role: string): string {
  return role === "admin"
    ? "This admin link has been replaced or taken away. Ask an admin for a new one."
    : "This link has been replaced. Ask an admin for the new one.";
}

export class WsTransport implements Transport {
  private ws: WebSocket | null = null;
  private attempts = 0;
  /** Tries since the socket was last open, and whether this run of them has asked about the link. */
  private refused = 0;
  private asked = false;
  private closed = false;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private onMessage: ((m: ServerMessage) => void) | null = null;
  private onStatus: ((s: Status) => void) | null = null;

  constructor(private readonly url: string) {}

  connect(onMessage: (m: ServerMessage) => void, onStatus: (s: Status) => void): void {
    this.onMessage = onMessage;
    this.onStatus = onStatus;
    this.closed = false;
    this.listen(true);
    this.open();
  }

  /** The network is back: try now, not at the end of the wait, and start the waits short again. */
  private readonly whenOnline = (): void => this.openNow(true);
  /** The network has gone: say so now, rather than when the socket finally notices. */
  private readonly whenOffline = (): void => {
    if (this.closed) return;
    this.detach();
    this.onStatus?.("offline");
    this.retry();
  };
  /** The screen is looked at again (a phone woken up): try now if it is not connected. */
  private readonly whenSeen = (): void => {
    if (document.visibilityState === "visible") this.openNow(false);
  };

  private listen(on: boolean): void {
    if (typeof window === "undefined") return;
    if (on) {
      window.addEventListener("online", this.whenOnline);
      window.addEventListener("offline", this.whenOffline);
      document.addEventListener("visibilitychange", this.whenSeen);
    } else {
      window.removeEventListener("online", this.whenOnline);
      window.removeEventListener("offline", this.whenOffline);
      document.removeEventListener("visibilitychange", this.whenSeen);
    }
  }

  /** Open a socket now unless one is open or opening. */
  private openNow(fresh: boolean): void {
    if (this.closed) return;
    const ws = this.ws;
    if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) return;
    if (fresh) this.attempts = 0;
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    this.open();
  }

  /** The picture address, made from the socket's own: same server, same tournament. */
  private photoAddress(playerId: string): string {
    const u = new URL(this.url.replace(/^ws/, "http"));
    const q = new URLSearchParams({ t: u.searchParams.get("t") ?? "main" });
    return `${u.origin}/api/photo/${encodeURIComponent(playerId)}?${q.toString()}`;
  }

  async uploadPhoto(playerId: string, jpeg: Blob, session: string | null): Promise<string | null> {
    if (!session) return "Type your PIN first.";
    try {
      const res = await fetch(this.photoAddress(playerId), {
        method: "POST",
        body: jpeg,
        // in a header, not the address, so it is never written down in a log
        headers: { "content-type": "image/jpeg", "x-bd-session": session },
      });
      const data = (await res.json().catch(() => null)) as { ok?: boolean; reason?: string } | null;
      if (res.ok && data?.ok) return null;
      return data?.reason ?? "The picture did not send. Try again.";
    } catch {
      return "No connection. Try again in a moment.";
    }
  }

  photoUrl(playerId: string, version: number): string {
    return `${this.photoAddress(playerId)}&v=${version}`;
  }

  private open(): void {
    if (this.closed) return;
    this.onStatus?.("connecting");
    let ws: WebSocket;
    try {
      ws = new WebSocket(this.url);
    } catch {
      this.retry();
      return;
    }
    this.ws = ws;
    let opened = false;
    // closing a socket that is still opening fails it, and its onclose tries again
    const giveUp = setTimeout(() => {
      if (!opened && this.ws === ws) ws.close();
    }, OPEN_WITHIN);
    ws.onopen = () => {
      clearTimeout(giveUp);
      opened = true;
      this.attempts = 0;
      this.refused = 0;
      this.asked = false;
      this.onStatus?.("open");
    };
    ws.onmessage = (ev) => {
      let parsed: ServerMessage;
      try {
        parsed = JSON.parse(String(ev.data)) as ServerMessage;
      } catch {
        return;
      }
      this.onMessage?.(parsed);
    };
    ws.onclose = () => {
      clearTimeout(giveUp);
      if (this.ws === ws) this.ws = null;
      this.onStatus?.("offline");
      if (!opened) this.refused += 1;
      if (this.refused >= ASK_AFTER && !this.asked) void this.askLink();
      this.retry();
    };
    ws.onerror = () => {
      // onclose follows; nothing to do here
    };
  }

  /**
   * A browser never says why a websocket was turned away, so ask the same address over plain HTTP:
   * 403 means the link has stopped (taken away or replaced while this screen was not connected),
   * and the screen is told in the same words as one that was open at the time. Anything else (no
   * network, the server busy) says nothing, and the tries go on.
   */
  private async askLink(): Promise<void> {
    this.asked = true;
    try {
      const res = await fetch(this.url.replace(/^ws/, "http"), { cache: "no-store" });
      if (res.status !== 403 || this.closed) return;
      const role = new URL(this.url.replace(/^ws/, "http")).pathname.split("/").filter(Boolean)[1] ?? "";
      this.onMessage?.({ kind: "error", reason: stoppedReason(role) });
    } catch {
      // offline: the next run of refusals asks again
      this.asked = false;
    }
  }

  private retry(): void {
    if (this.closed) return;
    this.attempts += 1;
    const delay = Math.min(10_000, 500 * 2 ** Math.min(this.attempts, 5)) + Math.random() * 300;
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => this.open(), delay);
  }

  send(m: ClientMessage): boolean {
    const ws = this.ws;
    if (!ws || ws.readyState !== WebSocket.OPEN) return false;
    try {
      ws.send(JSON.stringify(m));
      return true;
    } catch {
      return false;
    }
  }

  restart(): void {
    if (this.closed) return;
    this.detach();
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    this.attempts = 0;
    this.onStatus?.("offline");
    this.open();
  }

  /** Let go of the socket: its handlers go first, so its close is not taken for a refusal or a try. */
  private detach(): void {
    const ws = this.ws;
    this.ws = null;
    if (!ws) return;
    ws.onopen = null;
    ws.onmessage = null;
    ws.onclose = null;
    ws.onerror = null;
    try {
      ws.close();
    } catch {
      // already gone
    }
  }

  close(): void {
    this.closed = true;
    this.listen(false);
    if (this.timer) clearTimeout(this.timer);
    this.ws?.close();
    this.ws = null;
  }
}

/** The websocket address for a screen, from where the page was loaded. */
export function wsUrl(role: string, token: string, court: number | undefined, tournament: string): string {
  const proto = location.protocol === "https:" ? "wss:" : "ws:";
  const path = court ? `/ws/${role}/${token}/${court}` : `/ws/${role}/${token}`;
  return `${proto}//${location.host}${path}?t=${encodeURIComponent(tournament)}`;
}
